import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ShieldAlert, Info } from 'lucide-react';
import { useUser } from '@/lib/UserContext';
import api from '@/lib/api';

/**
 * Wrap any page with <TrustScoreGate mode="hard"> or mode="soft" to enforce
 * a minimum Data Trust score before rendering children. Super admins bypass.
 *
 * - mode="hard": below threshold → children are NOT rendered; remediation card shown
 * - mode="soft": below threshold → banner shown ABOVE children; children still render
 * - threshold defaults to 70
 */
export function TrustScoreGate({ children, mode = 'hard', threshold = 70, moduleLabel }) {
  const user = useUser();
  const isSuperAdmin = (user?.roles || []).includes('super_admin');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get('/analytics/trust-score')
      .then(res => { if (active) setData(res.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  // Super admins always see the module; no gating applied.
  if (isSuperAdmin) return <>{children}</>;
  if (loading || !data || !data.has_data) return <>{children}</>;

  const passed = data.score >= threshold;
  if (passed) return <>{children}</>;

  if (mode === 'soft') {
    return (
      <>
        <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/30 mb-3" data-testid="trust-gate-soft">
          <Info className="h-4 w-4" />
          <AlertTitle>Data Trust below {threshold}: results may be unreliable</AlertTitle>
          <AlertDescription>
            Current score {data.score} ({data.grade}). {moduleLabel} is showing anyway, but
            numbers should be validated before use in exec materials.
          </AlertDescription>
        </Alert>
        {children}
      </>
    );
  }

  // Hard block
  return (
    <Card className="border-rose-300 bg-rose-50 dark:bg-rose-950/20" data-testid="trust-gate-hard">
      <CardHeader className="flex flex-row items-start gap-3">
        <ShieldAlert className="w-6 h-6 text-rose-600 shrink-0 mt-0.5" />
        <div>
          <CardTitle className="text-rose-900 dark:text-rose-100">
            {moduleLabel} blocked: Data Trust {data.score} is below {threshold} ({data.grade})
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            This module produces benchmark / forecast numbers that downstream consumers
            rely on. Please resolve the items below in DataRefinery, re-upload, and try again.
          </p>
        </div>
      </CardHeader>
      <CardContent>
        <h3 className="font-semibold text-sm mb-2">Actions to unlock this module:</h3>
        <ul className="space-y-2 text-sm">
          {(data.remediation || []).map((r, i) => (
            <li key={i} className="flex items-start gap-2" data-testid={`trust-gate-reason-${r.signal}`}>
              <span className="font-mono text-xs bg-rose-200 dark:bg-rose-900 px-1.5 py-0.5 rounded shrink-0">
                {r.current_pct}%
              </span>
              <span className="text-muted-foreground">{r.hint}</span>
            </li>
          ))}
          {(!data.remediation || data.remediation.length === 0) && (
            <li className="text-muted-foreground">
              Score is driven by signals not yet surfaced individually. Re-upload a fresher
              DataRefinery export to refresh the metric.
            </li>
          )}
        </ul>
      </CardContent>
    </Card>
  );
}
